import { useState } from 'preact/hooks';
import { RichHtmlField, trimEmptyHtml } from '@components/minidoc/rich-html-field';
import { rpx } from 'client/lib/rpx-client';
import { BtnPrimary, BtnSecondary } from '@components/buttons';
import { showToast } from '@components/toaster';
import { MiraseeCheckoutConfig } from 'server/types/pmts-overrides';
import { pickLogo } from 'client/courses/pages/admin-branding/pick-logo';

type Props = {
  courseId: string;
  priceId: string;
  existingConfig?: MiraseeCheckoutConfig;
  hideWelcomeSection?: boolean;
  onSaved(config: MiraseeCheckoutConfig): void;
};

function emptyConfig(): MiraseeCheckoutConfig {
  return {
    templateMode: 'mirasee-checkout',
    hero: {
      headline: '',
    },
    benefits: {
      heading: "Here's What You'll Get:",
      items: [{ bold: '', text: '' }],
    },
    testimonials: [],
  };
}

function Field({ label, children }: { label: string; children: preact.ComponentChildren }) {
  return (
    <div class="flex flex-col gap-1">
      <label class="text-sm font-medium text-gray-700">{label}</label>
      {children}
    </div>
  );
}

function SectionHeader({ title }: { title: string }) {
  return (
    <h3 class="text-base font-semibold text-gray-800 border-b border-gray-200 pb-2 mt-4">
      {title}
    </h3>
  );
}

function ImageUploadField({
  label,
  value,
  onChange,
  imgClass,
}: {
  label: string;
  value?: string;
  onChange(url: string | undefined): void;
  imgClass?: string;
}) {
  async function handleUpload() {
    const result = await pickLogo({ cropRatio: undefined, requireCrop: false });
    if (result) {
      onChange(result.publicUrl);
    }
  }

  return (
    <Field label={label}>
      <div class="flex items-center gap-3">
        {value ? (
          <div class="flex items-center gap-3 flex-1 min-w-0">
            <img src={value} alt="" class={imgClass || 'h-10 max-w-40 object-contain rounded'} />
            <span class="text-xs text-gray-400 truncate flex-1">{value}</span>
            <BtnSecondary class="text-xs shrink-0" onClick={handleUpload}>
              Change
            </BtnSecondary>
            <button
              type="button"
              class="text-red-500 hover:text-red-700 text-xs shrink-0"
              onClick={() => onChange(undefined)}
            >
              Remove
            </button>
          </div>
        ) : (
          <BtnSecondary class="text-sm" onClick={handleUpload}>
            Upload Image
          </BtnSecondary>
        )}
      </div>
    </Field>
  );
}

export function MiraseeCheckoutForm({
  courseId,
  priceId,
  existingConfig,
  hideWelcomeSection,
  onSaved,
}: Props) {
  const [config, setConfig] = useState<MiraseeCheckoutConfig>(existingConfig || emptyConfig());
  const [isSaving, setIsSaving] = useState(false);

  function updateHero(updates: Partial<MiraseeCheckoutConfig['hero']>) {
    setConfig((c) => ({ ...c, hero: { ...c.hero, ...updates } }));
  }

  function updateBenefits(updates: Partial<NonNullable<MiraseeCheckoutConfig['benefits']>>) {
    setConfig((c) => ({
      ...c,
      benefits: {
        ...c.benefits,
        heading: c.benefits?.heading,
        items: c.benefits?.items || [],
        ...updates,
      },
    }));
  }

  function setBenefitItem(index: number, updates: Partial<{ bold: string; text: string }>) {
    setConfig((c) => {
      const items = [...(c.benefits?.items || [])];
      items[index] = { ...items[index], ...updates };
      return { ...c, benefits: { ...c.benefits, items } };
    });
  }

  function addBenefitItem() {
    setConfig((c) => ({
      ...c,
      benefits: { ...c.benefits, items: [...(c.benefits?.items || []), { bold: '', text: '' }] },
    }));
  }

  function removeBenefitItem(index: number) {
    setConfig((c) => ({
      ...c,
      benefits: { ...c.benefits, items: (c.benefits?.items || []).filter((_, i) => i !== index) },
    }));
  }

  function setTestimonial(
    index: number,
    updates: Partial<NonNullable<MiraseeCheckoutConfig['testimonials']>[0]>,
  ) {
    setConfig((c) => {
      const testimonials = [...(c.testimonials || [])];
      testimonials[index] = { ...testimonials[index], ...updates };
      return { ...c, testimonials };
    });
  }

  function addTestimonial() {
    setConfig((c) => ({
      ...c,
      testimonials: [...(c.testimonials || []), { quote: '', name: '' }],
    }));
  }

  function removeTestimonial(index: number) {
    setConfig((c) => ({
      ...c,
      testimonials: (c.testimonials || []).filter((_, i) => i !== index),
    }));
  }

  async function handleSave() {
    if (!config.hero.headline.trim()) {
      showToast({
        title: 'Missing headline',
        message: 'The hero headline is required.',
        type: 'warn',
      });
      return;
    }
    setIsSaving(true);
    try {
      // Normalize at save, never per keystroke: the HTML box is controlled, so
      // trimming on input eats a space or newline as it is typed.
      const saved: MiraseeCheckoutConfig = config.welcomePage
        ? {
            ...config,
            welcomePage: {
              ...config.welcomePage,
              bodyHtml: trimEmptyHtml(config.welcomePage.bodyHtml),
            },
          }
        : config;
      await rpx.courseSettings.setPriceCheckoutConfig({
        courseId,
        priceId,
        checkoutPageConfig: saved,
      });
      showToast({ title: 'Saved', message: 'Mirasee checkout template saved.', type: 'ok' });
      onSaved(saved);
    } catch (err) {
      showToast({
        title: 'Save failed',
        message: err instanceof Error ? err.message : 'An error occurred',
        type: 'warn',
      });
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div class="flex flex-col gap-4">
      <SectionHeader title="Hero" />
      <ImageUploadField
        label="Logo"
        value={config.hero.logoUrl}
        onChange={(url) => updateHero({ logoUrl: url })}
        imgClass="h-10 max-w-48 object-contain"
      />
      <Field label="Headline">
        <textarea
          class="ruz-input"
          rows={2}
          value={config.hero.headline}
          placeholder="Your compelling headline..."
          onInput={(e) => updateHero({ headline: (e.target as HTMLTextAreaElement).value })}
        />
      </Field>
      <Field label="Subtitle">
        <textarea
          class="ruz-input"
          rows={2}
          value={config.hero.subtitle || ''}
          placeholder="Supporting text under the headline..."
          onInput={(e) =>
            updateHero({ subtitle: (e.target as HTMLTextAreaElement).value || undefined })
          }
        />
      </Field>

      <SectionHeader title="Price Badge" />
      <Field label="Badge Text">
        <input
          class="ruz-input"
          type="text"
          value={config.priceBadgeText || ''}
          placeholder="e.g., Special Offer: $497"
          onInput={(e) =>
            setConfig((c) => ({
              ...c,
              priceBadgeText: (e.target as HTMLInputElement).value || undefined,
            }))
          }
        />
      </Field>

      <SectionHeader title="Benefits" />
      <Field label="Section Heading">
        <input
          class="ruz-input"
          type="text"
          value={config.benefits?.heading || ''}
          placeholder="Here's What You'll Get:"
          onInput={(e) =>
            updateBenefits({ heading: (e.target as HTMLInputElement).value || undefined })
          }
        />
      </Field>
      {(config.benefits?.items || []).map((item, i) => (
        <div key={i} class="flex gap-2 items-start">
          <div class="flex-1 flex flex-col gap-1">
            <input
              class="ruz-input"
              type="text"
              value={item.bold}
              placeholder="Bold text"
              onInput={(e) => setBenefitItem(i, { bold: (e.target as HTMLInputElement).value })}
            />
            <input
              class="ruz-input"
              type="text"
              value={item.text || ''}
              placeholder="Description (optional)"
              onInput={(e) =>
                setBenefitItem(i, { text: (e.target as HTMLInputElement).value || undefined })
              }
            />
          </div>
          <button
            type="button"
            class="text-red-500 hover:text-red-700 text-sm mt-2"
            onClick={() => removeBenefitItem(i)}
          >
            Remove
          </button>
        </div>
      ))}
      <div>
        <BtnSecondary class="text-sm" onClick={addBenefitItem}>
          + Add Benefit
        </BtnSecondary>
      </div>

      <SectionHeader title="Testimonials" />
      {(config.testimonials || []).map((t, i) => (
        <div key={i} class="border border-gray-200 rounded-lg p-4 flex flex-col gap-2">
          <div class="flex justify-between items-center">
            <span class="text-sm font-medium text-gray-600">Testimonial {i + 1}</span>
            <button
              type="button"
              class="text-red-500 hover:text-red-700 text-sm"
              onClick={() => removeTestimonial(i)}
            >
              Remove
            </button>
          </div>
          <textarea
            class="ruz-input"
            rows={3}
            value={t.quote}
            placeholder="Quote text..."
            onInput={(e) => setTestimonial(i, { quote: (e.target as HTMLTextAreaElement).value })}
          />
          <div class="flex gap-2">
            <input
              class="ruz-input flex-1"
              type="text"
              value={t.name}
              placeholder="Name"
              onInput={(e) => setTestimonial(i, { name: (e.target as HTMLInputElement).value })}
            />
            <input
              class="ruz-input flex-1"
              type="text"
              value={t.title || ''}
              placeholder="Title (optional)"
              onInput={(e) =>
                setTestimonial(i, { title: (e.target as HTMLInputElement).value || undefined })
              }
            />
          </div>
          <ImageUploadField
            label="Photo"
            value={t.photoUrl}
            onChange={(url) => setTestimonial(i, { photoUrl: url })}
            imgClass="w-10 h-10 rounded-full object-cover"
          />
        </div>
      ))}
      <div>
        <BtnSecondary class="text-sm" onClick={addTestimonial}>
          + Add Testimonial
        </BtnSecondary>
      </div>

      <SectionHeader title="Footer" />
      <Field label="Terms HTML">
        <textarea
          class="ruz-input text-xs"
          rows={3}
          value={config.termsHtml || ''}
          placeholder="Terms and conditions text (HTML supported)..."
          onInput={(e) =>
            setConfig((c) => ({
              ...c,
              termsHtml: (e.target as HTMLTextAreaElement).value || undefined,
            }))
          }
        />
      </Field>
      <Field label="Disclaimer HTML">
        <textarea
          class="ruz-input text-xs"
          rows={3}
          value={config.disclaimerHtml || ''}
          placeholder="Footer disclaimer text (HTML supported)..."
          onInput={(e) =>
            setConfig((c) => ({
              ...c,
              disclaimerHtml: (e.target as HTMLTextAreaElement).value || undefined,
            }))
          }
        />
      </Field>
      <div class="flex gap-4">
        <Field label="Contact Phone">
          <input
            class="ruz-input"
            type="text"
            value={config.contactPhone || ''}
            placeholder="1-800-555-0100"
            onInput={(e) =>
              setConfig((c) => ({
                ...c,
                contactPhone: (e.target as HTMLInputElement).value || undefined,
              }))
            }
          />
        </Field>
        <Field label="Contact Email">
          <input
            class="ruz-input"
            type="text"
            value={config.contactEmail || ''}
            placeholder="support@example.com"
            onInput={(e) =>
              setConfig((c) => ({
                ...c,
                contactEmail: (e.target as HTMLInputElement).value || undefined,
              }))
            }
          />
        </Field>
      </div>
      <ImageUploadField
        label="Footer Logo"
        value={config.footerLogoUrl}
        onChange={(url) => setConfig((c) => ({ ...c, footerLogoUrl: url }))}
        imgClass="h-8 max-w-40 object-contain"
      />

      {hideWelcomeSection && (
        <div class="rounded-lg bg-blue-50 border border-blue-200 p-4 my-4 text-sm text-blue-900">
          <strong>Thank-You page is managed in the campaign wizard.</strong>
          <p class="mt-1 text-blue-800">
            For campaign courses, the Thank-You page is configured per-campaign and applies to all
            prices. Open the campaign wizard to edit it.
          </p>
        </div>
      )}
      {!hideWelcomeSection && <SectionHeader title="Welcome / Thank You Page" />}
      {!hideWelcomeSection && (
        <p class="text-sm text-gray-500">
          Configure a branded welcome page shown after purchase. If enabled, students stay on this
          page instead of being redirected. AIP members see a "Continue to AI Playground" button;
          non-members see an inline upsell.
        </p>
      )}
      {!hideWelcomeSection && (
        <>
          <label class="flex items-center gap-2 text-sm font-medium text-gray-700">
            <input
              type="checkbox"
              checked={!!config.welcomePage}
              onChange={(e) => {
                const checked = (e.target as HTMLInputElement).checked;
                setConfig((c) => ({
                  ...c,
                  welcomePage: checked
                    ? c.welcomePage || {
                        headline: `Welcome to ${config.hero.headline || 'the course'}!`,
                      }
                    : undefined,
                }));
              }}
            />
            Enable custom welcome page
          </label>
          {config.welcomePage && (
            <div class="flex flex-col gap-4 pl-4 border-l-2 border-blue-200">
              <Field label="Headline">
                <input
                  class="ruz-input"
                  type="text"
                  value={config.welcomePage.headline}
                  placeholder="Welcome to the course!"
                  onInput={(e) =>
                    setConfig((c) => ({
                      ...c,
                      welcomePage: {
                        ...c.welcomePage!,
                        headline: (e.target as HTMLInputElement).value,
                      },
                    }))
                  }
                />
              </Field>
              <Field label="Subtitle">
                <input
                  class="ruz-input"
                  type="text"
                  value={config.welcomePage.subtitle || ''}
                  placeholder="Your enrollment is confirmed."
                  onInput={(e) =>
                    setConfig((c) => ({
                      ...c,
                      welcomePage: {
                        ...c.welcomePage!,
                        subtitle: (e.target as HTMLInputElement).value || undefined,
                      },
                    }))
                  }
                />
              </Field>
              <RichHtmlField
                label="Body"
                labelClass="text-sm font-medium text-gray-700"
                placeholderText="We're thrilled to have you on board..."
                textareaClass="ruz-input text-xs font-mono min-h-32"
                textareaRows={4}
                value={config.welcomePage.bodyHtml || ''}
                onChange={(html) =>
                  setConfig((c) => ({
                    ...c,
                    welcomePage: {
                      ...c.welcomePage!,
                      bodyHtml: html,
                    },
                  }))
                }
              />
              <div class="flex gap-4">
                <Field label="CTA Button Text">
                  <input
                    class="ruz-input"
                    type="text"
                    value={config.welcomePage.ctaText || ''}
                    placeholder="Continue to AI Playground →"
                    onInput={(e) =>
                      setConfig((c) => ({
                        ...c,
                        welcomePage: {
                          ...c.welcomePage!,
                          ctaText: (e.target as HTMLInputElement).value || undefined,
                        },
                      }))
                    }
                  />
                </Field>
                <Field label="CTA URL (fallback)">
                  <input
                    class="ruz-input"
                    type="text"
                    value={config.welcomePage.ctaUrl || ''}
                    placeholder="/community"
                    onInput={(e) =>
                      setConfig((c) => ({
                        ...c,
                        welcomePage: {
                          ...c.welcomePage!,
                          ctaUrl: (e.target as HTMLInputElement).value || undefined,
                        },
                      }))
                    }
                  />
                </Field>
              </div>
            </div>
          )}
        </>
      )}

      <div class="pt-4 border-t border-gray-200">
        <BtnPrimary disabled={isSaving} onClick={handleSave}>
          {isSaving ? 'Saving...' : 'Save Mirasee Template'}
        </BtnPrimary>
      </div>
    </div>
  );
}
